/**
 * Client-side search history for image search.
 *
 * The whole store lives in ONE localStorage key. Nothing here touches the
 * server: history is a per-browser convenience cache, deliberately not a
 * system of record — the share feature already covers deliberate persistence,
 * and per-visitor server storage on this bot-attacked endpoint would turn
 * every scripted request into database rows.
 *
 * Every read/write is wrapped: quota overflow or unavailable storage (old
 * Safari private mode throws on setItem) degrades to "no history" without
 * ever breaking search itself.
 *
 * Identity is one entry per src. Re-searching a recorded src UPDATES that
 * entry (results, filters, re-parent) instead of inserting a sibling, so
 * findBySrc is unique by construction and a Back/Forward or trail visit can
 * never replay the wrong entry's chain. Upload entries have no src and are
 * only ever addressed by id. Text entries have no src either — their
 * identity is the query PLUS the filters (the same words filtered to Malibu
 * are a different search than unfiltered), matched via querySignature.
 * The `q` field is additive: VERSION stays 1, old entries simply lack it.
 *
 * `sig` (additive too, VERSION stays 1) is the src's server-minted search
 * signature — pure TRANSPORT, never identity: findBySrc matches on src
 * alone, because every valid sig for a src is the same string and a
 * pre-signing entry is still the same search. Entries without one replay
 * fine from snapshot; only a live re-run then degrades to the server's 403.
 */

const KEY = "il-image-search-history";
// Bump when the entry shape changes incompatibly: a mismatched store is
// discarded wholesale rather than migrated — history is a cache, losing it
// is harmless, and migration code for a cache is carrying cost.
const VERSION = 1;
const CAP = 20;

function load() {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== VERSION || !Array.isArray(parsed.entries)) return [];
        return parsed.entries;
    } catch (e) {
        return [];
    }
}

function save(entries) {
    try {
        window.localStorage.setItem(KEY, JSON.stringify({ version: VERSION, entries }));
        return true;
    } catch (e) {
        // Quota or unavailable storage: drop the write, keep the search alive.
        return false;
    }
}

function randomId() {
    // Collision space only needs to cover 20 concurrent entries in one
    // browser — no need for UUID ceremony.
    return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Newest-activity-first — this is the Recent panel's display order. */
export function list() {
    return load().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export function findById(id) {
    if (!id) return null;
    return load().find((e) => e.id === id) || null;
}

export function findBySrc(src) {
    if (!src) return null;
    return load().find((e) => e.src === src) || null;
}

function querySignature(q, filters) {
    return q + '|' + JSON.stringify(filters || {});
}

export function findByQuery(q, filters) {
    if (!q) return null;
    const sig = querySignature(q, filters);
    return load().find((e) => e.q && querySignature(e.q, e.filters) === sig) || null;
}

/**
 * Record a successful search. Updates in place when the search is already
 * recorded — matched by `id` (how upload entries, which have no src, are
 * re-recorded on filter changes) or by src (one entry per src); otherwise
 * inserts and prunes the least-recently-updated entries beyond the cap.
 *
 * `parentId` semantics: `undefined` preserves the entry's existing chain (a
 * filter tweak is not a new hop), a string re-parents to the chain the user
 * is walking NOW (find-similar), `null` makes the entry explicitly
 * parentless (a fresh search).
 *
 * Returns the entry (never throws), or null when storage is unavailable —
 * callers treat null as "history is off" and carry on.
 */
export function record({ id, src, q, sig, thumb, filters, results, strongDistance, excludeLocationId, parentId }) {
    const entries = load();
    const now = Date.now();

    let entry = (id && entries.find((e) => e.id === id))
        || (src && entries.find((e) => e.src === src))
        // Text entries dedupe on query+filters: re-running the same words
        // with the same filters updates in place; the same words with
        // DIFFERENT filters are a distinct search.
        || (q && !src && entries.find((e) => e.q && querySignature(e.q, e.filters) === querySignature(q, filters)))
        || null;

    if (entry) {
        entry.filters = filters;
        entry.results = results;
        // Only ever upgrade: a sigless re-record (e.g. a pre-signing replay
        // path) must not erase a signature the entry already carries.
        if (sig) entry.sig = sig;
        entry.strongDistance = strongDistance;
        entry.excludeLocationId = excludeLocationId != null ? excludeLocationId : null;
        if (parentId !== undefined && parentId !== entry.id) {
            // Re-parent to the chain the user is walking NOW. Without this, a
            // revisit through a different find-similar path would show the
            // trail of a chain the user didn't walk. The self-check matters:
            // Find similar on the search's own source image lands back on the
            // SAME src, and a self-parent would loop the trail.
            entry.parentId = parentId || null;
        }
        entry.updatedAt = now;
    } else {
        entry = {
            id: randomId(),
            src: src || null,
            q: q || null,
            sig: sig || null,
            thumb: thumb || null,
            filters,
            results,
            strongDistance,
            excludeLocationId: excludeLocationId != null ? excludeLocationId : null,
            parentId: parentId || null,
            scrollY: null,
            createdAt: now,
            updatedAt: now,
        };
        entries.push(entry);
    }

    if (entries.length > CAP) {
        // Prune by least-recent ACTIVITY, not creation: an old search the
        // user keeps returning to outlives a newer one they abandoned.
        entries.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        entries.length = CAP;
    }

    if (save(entries)) return entry;

    // Quota pressure: measured ~73KB per entry on a large result set, so a
    // full store can brush the ~5MB budget when other keys share the origin.
    // Trade breadth for the newest entries rather than losing the write.
    entries.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    entries.length = Math.min(entries.length, Math.ceil(CAP / 2));
    return save(entries) ? entry : null;
}

/**
 * Silent field update — no updatedAt bump. Used for scrollY capture, which
 * must not reorder the Recent panel (replaying/leaving a search is not new
 * search activity).
 */
export function patch(id, fields) {
    const entries = load();
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;
    Object.assign(entry, fields);
    save(entries);
}

/**
 * The one URL that revisits this entry: shareable ?src= for URL searches,
 * shareable ?q= (plus its filters) for text searches, browser-local ?hist=
 * for uploads. Every surface (trail, Recent panel) derives links through
 * here so the three URL shapes stay in one place.
 *
 * Text URLs carry the entry's FILTERS too — the URL is the share link
 * (KTD4/KTD6), so it must describe the whole search, not just the words.
 */
export function entryHref(entry) {
    if (entry.src) {
        return '/?src=' + encodeURIComponent(entry.src)
            + (entry.sig ? '&sig=' + encodeURIComponent(entry.sig) : '');
    }
    if (entry.q) {
        const params = new URLSearchParams();
        params.set('q', entry.q);
        const f = entry.filters || {};
        (f.category_ids || []).forEach((id) => params.append('category_ids[]', id));
        (f.permit_ids || []).forEach((id) => params.append('permit_ids[]', id));
        if (f.city) params.set('city', f.city);
        if (f.county) params.set('county', f.county);
        if (f.near && f.radius) {
            params.set('near', f.near);
            params.set('radius', f.radius);
        }
        return '/?' + params.toString();
    }
    return '/?hist=' + entry.id;
}

/**
 * The inverse of makeThumbnail's data URL, for re-sharing a replayed upload:
 * the share endpoint accepts file uploads, not data URLs. Lives here so the
 * encode and decode of the thumbnail format stay in one module.
 */
export function dataUrlToFile(dataUrl, filename) {
    const comma = dataUrl.indexOf(',');
    const mime = (dataUrl.slice(0, comma).match(/data:([^;]+)/) || [])[1] || 'image/jpeg';
    const bin = atob(dataUrl.slice(comma + 1));
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new File([arr], filename, { type: mime });
}

export function clear() {
    try {
        window.localStorage.removeItem(KEY);
    } catch (e) {
        // Nothing to do — the store was unreadable anyway.
    }
}

/**
 * Downscale an uploaded File to a small JPEG data URL (~320px longest edge).
 *
 * This is the ONLY image we keep for an upload: the original file is
 * deliberately not stored (a decided trade — multi-MB blobs per upload vs a
 * ~10-20KB thumbnail). It serves the trail/Recent UI and becomes the share
 * reference image when a replayed upload is shared.
 *
 * Resolves null on any failure (unreadable file, canvas tainting, decode
 * error) — the entry then simply has no thumbnail.
 */
export function makeThumbnail(file) {
    return new Promise((resolve) => {
        try {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                try {
                    const longest = Math.max(img.width, img.height) || 1;
                    const scale = Math.min(1, 320 / longest);
                    const canvas = document.createElement("canvas");
                    canvas.width = Math.max(1, Math.round(img.width * scale));
                    canvas.height = Math.max(1, Math.round(img.height * scale));
                    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL("image/jpeg", 0.7));
                } catch (e) {
                    resolve(null);
                } finally {
                    URL.revokeObjectURL(url);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                resolve(null);
            };
            img.src = url;
        } catch (e) {
            resolve(null);
        }
    });
}
